import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  StatusBadge,
  FiltersTabs,
  IconButton,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
} from '../components/ui';
import { AdjustmentsHorizontalIcon, ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

enum StatusFilter {
  All = 'all',
  Pending = 'pending',
  Confirmed = 'confirmed',
  Rejected = 'rejected',
  Cancelled = 'cancelled',
}

type ManualActionPayload = {
  paymentId: number;
  action: 'confirm' | 'reject';
};

interface Payment {
  id: number;
  operation_token?: string | null;
  shop_process_id?: string | null;
  customer_name?: string;
  amount: number;
  reserved_amount: number;
  currency: string;
  status: string;
  created_at: string;
  expires_at: string;
  provider_tx_id?: string;
  manual?: number;
  processed_by?: number;
  processed_by_username?: string;
  processed_by_role?: string;
  processed_at?: string;
  processing_type?: string;
  processing_notes?: string;
  sender_name?: string;
  sender_account?: string;
  callback_status?: string | null;
  callback_attempt_count?: number | null;
  callback_last_error?: string | null;
  callback_last_http_status?: number | null;
  callback_last_response_at?: string | null;
  callback_last_response_body?: string | null;
}

const paymentExternalId = (payment: Payment) => payment.shop_process_id || payment.operation_token || null;
const paymentDisplayId = (payment: Payment) => paymentExternalId(payment) || String(payment.id);

interface PaymentDetails {
  payment: Payment;
  logs: any[];
}

const statusOrder: StatusFilter[] = [
  StatusFilter.All,
  StatusFilter.Pending,
  StatusFilter.Confirmed,
  StatusFilter.Rejected,
  StatusFilter.Cancelled,
];

const statusLabels: Record<StatusFilter, string> = {
  [StatusFilter.All]: 'Все',
  [StatusFilter.Pending]: 'Ожидают',
  [StatusFilter.Confirmed]: 'Подтверждены',
  [StatusFilter.Rejected]: 'Отклонены',
  [StatusFilter.Cancelled]: 'Отменены',
};

const statusTone: Record<string, Parameters<typeof StatusBadge>[0]['tone']> = {
  pending: 'warning',
  confirmed: 'success',
  rejected: 'danger',
  cancelled: 'default',
};

const statusText: Record<string, string> = {
  pending: 'Ожидает',
  confirmed: 'Подтвержден',
  rejected: 'Отклонен',
  cancelled: 'Отменен',
  expired: 'Истек',
};

const callbackSummary = (payment: Payment) => {
  if (!payment.callback_status) return null;
  const status = payment.callback_status;
  const http = payment.callback_last_http_status != null ? `HTTP ${payment.callback_last_http_status}` : '';
  const failures = payment.callback_attempt_count ?? null;
  const attemptsTotal =
    failures == null
      ? null
      : status === 'sent'
        ? failures + 1
        : failures;
  const attempts = attemptsTotal != null ? `попыток: ${attemptsTotal}` : '';
  const statusText = status === 'sent' ? 'Доставлен' : status === 'failed' ? 'Не доставлен' : 'В очереди';
  return [statusText, http, attempts].filter(Boolean).join(' · ');
};

const callbackIndicator = (status?: string | null) => {
  if (!status) return { label: '—', cls: 'bg-slate-300', title: 'Callback не настроен / не ставился в очередь' };
  if (status === 'sent') return { label: 'OK', cls: 'bg-emerald-500', title: 'Магазин ответил HTTP 2xx (callback доставлен)' };
  // pending / failed / anything else
  return { label: 'ERR', cls: 'bg-rose-500', title: 'Callback не подтверждён (ещё не доставлен или ошибка/ретраи)' };
};

const currencySymbols: Record<string, string> = {
  RUB: '₽',
  KZT: '₸',
  USD: '$',
  EUR: '€',
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const amountWithCurrency = (amount: number, currency: string) =>
  `${amount.toLocaleString('ru-RU')} ${currencySymbols[currency] || currency}`;

const Payments: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigateRouter = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPayment, setSelectedPayment] = useState<PaymentDetails | null>(null);
  const [filter, setFilter] = useState<StatusFilter>(StatusFilter.All);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const pageSize = 50;
  const topPaymentIdRef = useRef<number | null>(null);

  type PaymentsResponse = {
    items: Payment[];
    meta: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };

  const paymentsQuery = useQuery<PaymentsResponse>({
    queryKey: ['payments', user?.role, page, pageSize],
    queryFn: async () => {
      const endpoint = user?.role === 'operator' ? '/operator/payments' : '/admin/payments';
      const { data } = await api.get(endpoint, {
        params: {
          meta: 1,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
      });
      return data as PaymentsResponse;
    },
    enabled: !!user && (user.role === 'admin' || user.role === 'operator'),
    refetchInterval: 8_000,
  });

  const payments = paymentsQuery.data?.items ?? [];
  const pageMeta = paymentsQuery.data?.meta;
  const lastPage = pageMeta?.total ? Math.max(1, Math.ceil(pageMeta.total / pageSize)) : 1;
  const isLoading = paymentsQuery.isLoading;

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  React.useEffect(() => {
    if (selectedPayment) {
      const { overflow } = document.body.style;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = overflow;
      };
    }
  }, [selectedPayment]);

  useEffect(() => {
    // Уведомляем о новом платеже только на первой странице (последние платежи)
    if (page !== 1) return;
    const topId = payments[0]?.id;
    if (!topId) return;
    if (topPaymentIdRef.current !== null && topPaymentIdRef.current !== topId) {
      try {
        window.dispatchEvent(new Event('notify:new_payment'));
      } catch {}
    }
    topPaymentIdRef.current = topId;
  }, [payments, page]);

  const manualMutation = useMutation<void, unknown, ManualActionPayload>({
    mutationFn: async ({ paymentId, action }) => {
      const endpoint = user?.role === 'operator' ? '/operator/payments/manual' : '/admin/payments/manual';
      await api.post(endpoint, { paymentId, action });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.error || 'Не удалось выполнить действие');
    },
  });

  const isSubmitting = manualMutation.isPending;

  const loadPaymentDetails = async (paymentId: number) => {
    try {
      const base = user?.role === 'operator' ? '/operator/payments/' : '/admin/payments/';
      const response = await api.get(`${base}${paymentId}`);
      setSelectedPayment(response.data);
    } catch (error) {
      console.error('Failed to load payment details:', error);
    }
  };

  const handleManualAction = (paymentId: number, action: 'confirm' | 'reject') => {
    if (user?.role !== 'admin' && user?.role !== 'operator') return;
    manualMutation.mutate({ paymentId, action });
  };

  useEffect(() => {
    const state = location.state as { initialSearch?: string; paymentId?: number } | null;
    if (!state) return;
    if (state.initialSearch) {
      setSearch(state.initialSearch);
      setFilter(StatusFilter.All);
    }
    if (state.paymentId) {
      loadPaymentDetails(state.paymentId);
    }
    navigateRouter(location.pathname, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filteredPayments = useMemo(() => {
    const rawSearch = search.trim();
    const normalizedSearch = rawSearch.toLowerCase();
    const normalizedDigits = normalizedSearch.replace(/\D+/g, '');
    const rawDigits = rawSearch.replace(/\D+/g, '');
    return payments.filter((payment) => {
      const matchesFilter = filter === StatusFilter.All || payment.status === filter;
      if (!normalizedSearch) {
        return matchesFilter;
      }

      const searchableText = [
        String(payment.id),
        payment.shop_process_id ?? '',
        payment.operation_token ?? '',
        payment.customer_name ?? '',
        payment.provider_tx_id ?? '',
        payment.sender_name ?? '',
        payment.sender_account ?? '',
        payment.processing_notes ?? '',
        payment.processed_by_username ?? '',
        payment.status ?? '',
        payment.currency ?? '',
        payment.processing_type ?? '',
        payment.manual ? 'manual' : '',
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());

      const amountValues = [
        payment.amount.toString(),
        payment.amount.toLocaleString('ru-RU'),
        amountWithCurrency(payment.amount, payment.currency),
        payment.reserved_amount.toString(),
        payment.reserved_amount.toLocaleString('ru-RU'),
        amountWithCurrency(payment.reserved_amount, payment.currency),
      ].map((value) => value.toString());

      const matchesText = searchableText.some((value) => value.includes(normalizedSearch));
      const matchesDigits =
        normalizedDigits.length > 0 &&
        [
          payment.provider_tx_id ?? '',
          payment.processing_notes ?? '',
          payment.sender_account ?? '',
          payment.processed_by_username ?? '',
        ]
          .filter(Boolean)
          .map((value) => value.replace(/\D+/g, ''))
          .some((value) => value.includes(normalizedDigits));

      const matchesAmount =
        amountValues.some((value) => value.toLowerCase().includes(normalizedSearch)) ||
        (rawSearch ? amountValues.some((value) => value.includes(rawSearch)) : false);
      const matchesAmountDigits =
        rawDigits.length > 0 &&
        amountValues
          .map((value) => value.replace(/\D+/g, ''))
          .some((value) => value.includes(rawDigits));

      return matchesFilter && (matchesText || matchesDigits || matchesAmount || matchesAmountDigits);
    });
  }, [payments, filter, search]);

  const filterTabs = useMemo(() => {
    const counts = payments.reduce<Record<StatusFilter, number>>(
      (acc, payment) => {
        if (statusOrder.includes(payment.status as StatusFilter)) {
          acc[payment.status as StatusFilter] += 1;
        }
        acc[StatusFilter.All] += 1;
        return acc;
      },
      {
        [StatusFilter.All]: 0,
        [StatusFilter.Pending]: 0,
        [StatusFilter.Confirmed]: 0,
        [StatusFilter.Rejected]: 0,
        [StatusFilter.Cancelled]: 0,
      }
    );

    return statusOrder.map((key) => ({ key, label: statusLabels[key], badge: counts[key] }));
  }, [payments]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">Управление платежами</h1>
          <p className="text-sm text-slate-500">Следите за статусами и вручную управляйте сложными кейсами.</p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            onClick={() => paymentsQuery.refetch()}
            aria-label="Обновить список"
            variant="ghost"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </IconButton>
        </div>
      </div>

      <Card>
        <CardHeader
          className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          title={<span className="text-lg font-semibold text-slate-900">Список платежей</span>}
          description="Используйте фильтры, чтобы сконцентрироваться на нужных статусах."
          actions={
            <FiltersTabs
              className="hidden md:inline-flex"
              tabs={filterTabs}
              value={filter}
              onChange={(key) => setFilter(key as StatusFilter)}
            />
          }
        />
        <CardBody className="space-y-4">
          {pageMeta ? (
            <div className="flex flex-col gap-2 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-3 text-sm text-[var(--app-text-muted)] shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div>
                {pageMeta.total > 0 ? (
                  <span>
                    Показаны{' '}
                    <span className="font-semibold text-[var(--app-text)]">
                      {pageMeta.offset + 1}-{Math.min(pageMeta.offset + pageMeta.limit, pageMeta.total)}
                    </span>{' '}
                    из <span className="font-semibold text-[var(--app-text)]">{pageMeta.total}</span>
                  </span>
                ) : (
                  <span>Платежи не найдены</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={page <= 1 || isLoading}
                  className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  В начало
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                  className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Назад
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--app-text-muted)]">Стр.</span>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const next = Math.min(
                          lastPage,
                          Math.max(1, Number.parseInt(pageInput, 10) || 1)
                        );
                        setPage(next);
                      }
                    }}
                    className="w-16 rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm text-[var(--app-text)] outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    aria-label="Номер страницы"
                  />
                  <span className="text-sm text-[var(--app-text-muted)]">из {lastPage}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = Math.min(lastPage, Math.max(1, Number.parseInt(pageInput, 10) || 1));
                      setPage(next);
                    }}
                    disabled={isLoading}
                    className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Перейти
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pageMeta.hasMore || isLoading}
                  className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Вперёд
                </button>
                <button
                  type="button"
                  onClick={() => setPage(lastPage)}
                  disabled={page >= lastPage || isLoading || !pageMeta.total}
                  className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-surface-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  В конец
                </button>
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-2 shadow-sm sm:hidden">
            <MagnifyingGlassIcon className="h-5 w-5 text-[var(--app-text-muted)]" />
            <input
              type="search"
              placeholder="Поиск по системе"
              className="w-full border-none bg-transparent text-sm text-[var(--app-text)] outline-none placeholder:text-[var(--app-text-muted)]"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="space-y-1 md:hidden">
            <label className="text-xs font-semibold uppercase text-secondary-500">Статус</label>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value as StatusFilter)}
              className="w-full rounded-full border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              {filterTabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label}
                  {tab.badge !== undefined ? ` (${tab.badge})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-3 md:hidden">
            {paymentsQuery.isLoading ? (
              <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-[var(--app-text-muted)]">
                Загружаем платежи...
              </div>
            ) : filteredPayments.length === 0 ? (
              <EmptyState
                title="Платежи не найдены"
                description="Попробуйте изменить фильтр или обновите список."
              />
            ) : (
              filteredPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="space-y-3 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-4 shadow-sm transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[var(--app-heading)]">#{paymentDisplayId(payment)}</p>
                      <p className="text-xs text-[var(--app-text-muted)]">
                        {payment.customer_name || 'Покупатель не указан'}
                      </p>
                      {payment.provider_tx_id ? (
                        <p className="text-xs text-[var(--app-text-muted)]">Транзакция: {payment.provider_tx_id}</p>
                      ) : null}
                    {payment.callback_status ? (
                      <p className="text-xs text-[var(--app-text-muted)]">
                        Callback: {payment.callback_status === 'sent' ? 'OK' : 'ERR'}
                      </p>
                    ) : null}
                    </div>
                    <StatusBadge tone={statusTone[payment.status] || 'default'}>
                      {statusText[payment.status] || payment.status}
                    </StatusBadge>
                  </div>
                  <div className="space-y-1 text-sm text-[var(--app-text)]">
                    <p className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)] whitespace-nowrap">
                      {amountWithCurrency(payment.amount, payment.currency)}
                    </p>
                    {payment.amount !== payment.reserved_amount ? (
                      <p className="text-xs text-[var(--app-text-muted)]">
                        Резерв: {amountWithCurrency(payment.reserved_amount, payment.currency)}
                      </p>
                    ) : null}
                    <p className="text-xs text-[var(--app-text-muted)]">
                      Создан: {formatDateTime(payment.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => loadPaymentDetails(payment.id)}
                      className="rounded-full border border-[var(--app-border)] px-3 py-1 text-xs font-semibold text-primary-600 transition hover:border-primary-200"
                    >
                      Детали
                    </button>
                    {(user?.role === 'admin' || user?.role === 'operator') && payment.status === 'pending' ? (
                      <>
                        <button
                          onClick={() => handleManualAction(payment.id, 'confirm')}
                          className="rounded-full border border-success/40 bg-success/10 px-3 py-1 text-xs font-semibold text-success transition hover:bg-success/20"
                          disabled={isSubmitting}
                        >
                          Подтвердить
                        </button>
                        <button
                          onClick={() => handleManualAction(payment.id, 'reject')}
                          className="rounded-full border border-danger/40 bg-danger/10 px-3 py-1 text-xs font-semibold text-danger transition hover:bg-danger/20"
                          disabled={isSubmitting}
                        >
                          Отклонить
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden md:block">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>ID / Покупатель</TableHeaderCell>
                  <TableHeaderCell>Сумма</TableHeaderCell>
                  <TableHeaderCell>Статус</TableHeaderCell>
                  <TableHeaderCell>Транзакция</TableHeaderCell>
                  <TableHeaderCell>Callback</TableHeaderCell>
                  <TableHeaderCell>Создан</TableHeaderCell>
                  <TableHeaderCell>Обработан</TableHeaderCell>
                  <TableHeaderCell numeric>Действия</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500">
                      Загружаем платежи...
                    </TableCell>
                  </TableRow>
                ) : filteredPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500">
                      Платежи с таким фильтром не найдены.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPayments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="max-w-[220px]">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-primary-50 text-primary-500">
                            {(payment.customer_name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 space-y-1">
                            <p className="text-sm font-semibold text-slate-900">#{paymentDisplayId(payment)}</p>
                            <p className="truncate text-sm text-slate-600">{payment.customer_name || 'Покупатель не указан'}</p>
                            {payment.sender_name ? (
                              <p className="truncate text-xs text-slate-500">Отправитель: {payment.sender_name}</p>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-sm text-slate-600">
                          <div className="text-base font-semibold leading-tight tracking-tight text-slate-900 whitespace-nowrap">
                            {amountWithCurrency(payment.amount, payment.currency)}
                          </div>
                          {payment.amount !== payment.reserved_amount ? (
                            <div className="text-xs text-slate-500">
                              Резерв: {amountWithCurrency(payment.reserved_amount, payment.currency)}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge tone={statusTone[payment.status] || 'default'}>
                          {statusText[payment.status] || payment.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm text-slate-600">
                          {payment.provider_tx_id ? <div>Провайдер: {payment.provider_tx_id}</div> : <div>—</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const c = callbackIndicator(payment.callback_status);
                          return (
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-block h-3 w-3 rounded-full ${c.cls}`}
                                title={c.title}
                                aria-label={c.title}
                              />
                              <span className="text-xs text-slate-500">{c.label}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-600">{formatDateTime(payment.created_at)}</div>
                        <div className="text-xs text-slate-400">Истекает: {formatDateTime(payment.expires_at)}</div>
                      </TableCell>
                      <TableCell>
                        {payment.processing_type === 'manual' ? (
                          payment.processed_by_username ? (
                            <div className="space-y-1 text-sm text-slate-600">
                              <div>{payment.processed_by_username}</div>
                              {payment.processed_at ? (
                                <div className="text-xs text-slate-500">{formatDateTime(payment.processed_at)}</div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )
                        ) : payment.processed_at || payment.status !== 'pending' ? (
                          <div className="space-y-1 text-sm text-slate-600">
                            <div>Система обработала</div>
                            <div className="text-xs text-slate-500">автоматически</div>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell numeric>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => loadPaymentDetails(payment.id)}
                            className="text-sm font-semibold text-primary-600 transition hover:text-primary-700"
                          >
                            Детали
                          </button>
                          {(user?.role === 'admin' || user?.role === 'operator') && payment.status === 'pending' ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleManualAction(payment.id, 'confirm')}
                                className="rounded-full bg-success/10 px-2.5 py-1 text-xs font-semibold text-success transition hover:bg-success/20"
                                disabled={isSubmitting}
                              >
                                Подтвердить
                              </button>
                              <button
                                onClick={() => handleManualAction(payment.id, 'reject')}
                                className="rounded-full bg-danger/10 px-2.5 py-1 text-xs font-semibold text-danger transition hover:bg-danger/20"
                                disabled={isSubmitting}
                              >
                                Отклонить
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </div>
        </CardBody>
      </Card>

      {selectedPayment ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:px-6">
          <div
            className="absolute inset-0 bg-[var(--app-bg)]/70 backdrop-blur-sm transition"
            onClick={() => setSelectedPayment(null)}
          />
          <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-lifted">
            <div className="flex items-center justify-between border-b border-[var(--app-border)] px-5 py-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">Детали платежа</p>
                <h2 className="text-base font-semibold text-[var(--app-heading)]">#{selectedPayment.payment.id}</h2>
              </div>
              <IconButton aria-label="Закрыть" onClick={() => setSelectedPayment(null)}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>
            <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-5 py-5 text-sm">
              <Card className="shadow-none border border-[var(--app-border)] bg-[var(--app-surface-subtle)]/70">
                <CardBody className="space-y-3 text-sm text-[var(--app-text)]">
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-secondary-500">Покупатель</p>
                    <p className="text-sm font-semibold text-[var(--app-heading)]">
                      {selectedPayment.payment.customer_name || 'Не указан'}
                    </p>
                    {selectedPayment.payment.sender_name ? (
                      <p className="text-xs text-[var(--app-text-muted)]">
                        Отправитель: {selectedPayment.payment.sender_name}
                        {selectedPayment.payment.sender_account ? ` (${selectedPayment.payment.sender_account})` : ''}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-secondary-500">Сумма</p>
                    <p className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)]">
                      {amountWithCurrency(selectedPayment.payment.amount, selectedPayment.payment.currency)}
                    </p>
                    {selectedPayment.payment.amount !== selectedPayment.payment.reserved_amount ? (
                      <p className="text-xs text-[var(--app-text-muted)]">
                        Резерв: {amountWithCurrency(selectedPayment.payment.reserved_amount, selectedPayment.payment.currency)}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-semibold uppercase text-secondary-500">Статус</p>
                    <StatusBadge tone={statusTone[selectedPayment.payment.status] || 'default'}>
                      {statusText[selectedPayment.payment.status] || selectedPayment.payment.status}
                    </StatusBadge>
                  </div>
                  <div className="grid grid-cols-1 gap-3 text-xs text-[var(--app-text-muted)]">
                    <div>
                      <p className="text-[11px] font-semibold uppercase text-secondary-500">Создан</p>
                      <p className="text-[var(--app-text)]">{formatDateTime(selectedPayment.payment.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase text-secondary-500">Истекает</p>
                      <p className="text-[var(--app-text)]">{formatDateTime(selectedPayment.payment.expires_at)}</p>
                    </div>
                    {selectedPayment.payment.provider_tx_id ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Транзакция провайдера</p>
                        <p className="font-medium text-primary-500">{selectedPayment.payment.provider_tx_id}</p>
                      </div>
                    ) : null}
                    {selectedPayment.payment.processed_by_username ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Обработал</p>
                        <p className="text-[var(--app-text)]">{selectedPayment.payment.processed_by_username}</p>
                        {selectedPayment.payment.processed_at ? (
                          <p className="text-xs text-[var(--app-text-muted)]">
                            {formatDateTime(selectedPayment.payment.processed_at)}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {selectedPayment.payment.processing_type ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Тип обработки</p>
                        <p className="font-semibold">
                          {selectedPayment.payment.processing_type === 'manual' ? (
                            <span className="text-orange-600">Ручная</span>
                          ) : (
                            <span className="text-green-600">Автоматическая</span>
                          )}
                        </p>
                      </div>
                    ) : null}
                    {selectedPayment.payment.processing_notes ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Примечания</p>
                        <p className="text-[var(--app-text)]">{selectedPayment.payment.processing_notes}</p>
                      </div>
                    ) : null}
                    {selectedPayment.payment.callback_status ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Callback в магазин</p>
                        <p className="text-[var(--app-text)]">
                          {callbackSummary(selectedPayment.payment) || '—'}
                        </p>
                        {selectedPayment.payment.callback_last_error ? (
                          <p className="mt-1 text-xs text-[var(--app-text-muted)]">{selectedPayment.payment.callback_last_error}</p>
                        ) : null}
                        {selectedPayment.payment.callback_last_response_body ? (
                          <p className="mt-1 whitespace-pre-wrap break-words rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-xs text-[var(--app-text)]">
                            {selectedPayment.payment.callback_last_response_body}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </CardBody>
              </Card>

              {selectedPayment.logs.length ? (
                <Card className="shadow-none border border-[var(--app-border)] bg-[var(--app-surface-subtle)]/70">
                  <CardHeader
                    title={<span className="text-[var(--app-heading)]">История событий</span>}
                    description="События аудита по данному платежу."
                  />
                  <CardBody className="max-h-48 space-y-2 overflow-y-auto text-xs">
                    {selectedPayment.logs.map((log, index) => (
                      <div
                        key={index}
                        className="rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)]/80 p-3 text-sm text-[var(--app-text)]"
                      >
                        <p className="font-semibold text-[var(--app-heading)]">{log.action}</p>
                        <p className="text-xs text-[var(--app-text-muted)]">
                          {formatDateTime(log.ts)} · {log.actor}
                        </p>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Payments;
